import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { QURAN_VERSES, getDailyVerse } from '../../data/quranVerses';
import { CrescentStarIcon, StarOrnament, RamadanLanternIcon, TasbihIcon, MosqueIcon } from '../common/IslamicIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';
const LIKED_KEY = 'liked_verses_local';
const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const NASKH = "'Amiri', Georgia, serif";

/** Rich animated Islamic/Ramadan background for verse card */
const VerseAnimatedBg = ({ isDarkMode }) => {
  const color = isDarkMode ? '#fbbf24' : 'var(--p-hex)';
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
      {/* Pulsing geometric star pattern */}
      <div className="absolute inset-0 verse-shimmer opacity-[0.045]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="vpGeo" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M20 2l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" fill={isDarkMode ? 'white' : 'black'} opacity="0.7" />
              <circle cx="0"  cy="0"  r="2" fill={isDarkMode ? 'white' : 'black'} opacity="0.3" />
              <circle cx="40" cy="40" r="2" fill={isDarkMode ? 'white' : 'black'} opacity="0.3" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#vpGeo)" />
        </svg>
      </div>

      {/* Ramadan lantern — top left, slow float */}
      <div className="absolute -top-1 left-3 verse-float-a" style={{ opacity: isDarkMode ? 0.22 : 0.13 }}>
        <RamadanLanternIcon size={28} color={color} />
      </div>

      {/* Crescent + star — top right */}
      <div className="absolute top-2 right-4 verse-float-b" style={{ opacity: isDarkMode ? 0.25 : 0.15 }}>
        <CrescentStarIcon size={22} color={color} />
      </div>

      {/* 8-point star — mid left */}
      <div className="absolute top-1/2 left-2 -translate-y-1/2 verse-float-c" style={{ opacity: isDarkMode ? 0.18 : 0.10 }}>
        <StarOrnament size={20} color={color} />
      </div>

      {/* Tasbih beads — bottom right */}
      <div className="absolute bottom-2 right-3 verse-float-a" style={{ animationDelay: '3.5s', opacity: isDarkMode ? 0.20 : 0.11 }}>
        <TasbihIcon size={26} color={color} />
      </div>

      {/* Mosque silhouette — bottom left */}
      <div className="absolute bottom-1 left-4 verse-float-b" style={{ animationDelay: '2s', opacity: isDarkMode ? 0.18 : 0.10 }}>
        <MosqueIcon size={24} color={color} />
      </div>

      {/* Large faint central star */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 verse-float-c" style={{ animationDelay: '6s', opacity: isDarkMode ? 0.07 : 0.04 }}>
        <StarOrnament size={56} color={color} />
      </div>

      {/* Small lantern — mid right */}
      <div className="absolute top-1/3 right-2 verse-float-b" style={{ animationDelay: '4s', opacity: isDarkMode ? 0.14 : 0.08 }}>
        <RamadanLanternIcon size={16} color={color} />
      </div>
    </div>
  );
};

/**
 * compact=true → full-text card above prayer list
 * compact=false → full standalone card
 */
const QuranVerseCard = ({ compact = false, box = false }) => {
  const { t } = useTranslation();
  const { isDarkMode, language, isRTL } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [currentVerse, setCurrentVerse] = useState(getDailyVerse);
  const [likedIds, setLikedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || '[]')); }
    catch { return new Set(); }
  });
  const [likeLoading, setLikeLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${API_URL}/api/verses/liked`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.liked_ids) {
          const s = new Set(data.liked_ids);
          setLikedIds(s);
          localStorage.setItem(LIKED_KEY, JSON.stringify([...s]));
        }
      })
      .catch(() => {});
  }, [isAuthenticated]);

  const isLiked = likedIds.has(currentVerse.id);

  const toggleLike = async () => {
    const newLiked = !isLiked;
    const newSet = new Set(likedIds);
    if (newLiked) newSet.add(currentVerse.id);
    else newSet.delete(currentVerse.id);
    setLikedIds(newSet);
    localStorage.setItem(LIKED_KEY, JSON.stringify([...newSet]));
    if (!isAuthenticated) return;
    setLikeLoading(true);
    try {
      await fetch(`${API_URL}/api/verses/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ verse_id: currentVerse.id, liked: newLiked }),
      });
    } catch (e) { console.error(e); }
    finally { setLikeLoading(false); }
  };

  const navigateVerse = (dir) => {
    const idx = QURAN_VERSES.findIndex(v => v.id === currentVerse.id);
    setCurrentVerse(QURAN_VERSES[(idx + dir + QURAN_VERSES.length) % QURAN_VERSES.length]);
  };

  const translation = currentVerse.translations[language] || currentVerse.translations.en;

  // ── Box mode (square, side-by-side on home) ─────────────────────────────────
  if (box) {
    return (
      <button
        data-testid="quran-verse-card"
        onClick={() => navigate('/quran')}
        className={`relative rounded-2xl overflow-hidden h-full w-full text-center ${
          isDarkMode
            ? 'bg-gradient-to-br from-[var(--card-dark-from)] to-[var(--card-dark-to)] border border-white/10 verse-glow'
            : 'bg-gradient-to-br from-[var(--bg-light-via)] to-white border border-[var(--p-hex)]/25 verse-glow-light'
        }`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <VerseAnimatedBg isDarkMode={isDarkMode} />
        <div className="absolute -bottom-8 -left-6 w-28 h-28 rounded-full pointer-events-none"
          style={{ background: isDarkMode ? '#fbbf24' : 'var(--p-hex)', opacity: isDarkMode ? 0.16 : 0.12, filter: 'blur(30px)' }} />
        <div className="relative z-10 p-3 flex flex-col h-full min-h-[128px]">
          <div className="flex items-center gap-1.5 mb-1.5">
            <CrescentStarIcon size={12} color={isDarkMode ? '#fbbf24' : 'var(--p-hex)'} />
            <span className={`text-[9px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-amber-400' : 'text-[var(--p-hex)]'}`}>
              {t('verse.verseOfDay')}
            </span>
          </div>
          <p
            className={`flex-1 flex items-center justify-center font-bold line-clamp-3 ${isDarkMode ? 'text-amber-50' : 'text-[var(--p-hex)]'}`}
            style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '0.95rem', lineHeight: 1.7 }}
          >
            {currentVerse.arabic}
          </p>
          <p className={`text-[10px] font-semibold tracking-wide mt-1.5 ${isDarkMode ? 'text-amber-400/75' : 'text-[var(--p-hex)]/60'}`}>
            ﴾ {currentVerse.reference} ﴿
          </p>
        </div>
      </button>
    );
  }

  // ── Compact mode ──────────────────────────────────────────────────────────
  if (compact) {
    return (
      <div
        data-testid="quran-verse-card"
        className={`relative rounded-2xl overflow-hidden ${
          isDarkMode
            ? `bg-gradient-to-br from-[var(--card-dark-from)] to-[var(--card-dark-to)] border border-white/10 verse-glow`
            : `bg-gradient-to-br from-[var(--bg-light-from)] to-[var(--bg-light-via)] border border-[var(--p-hex)]/12 verse-glow-light`
        }`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <VerseAnimatedBg isDarkMode={isDarkMode} />

        <div className="relative z-10 px-4 pt-2 pb-2">
          {/* Header row */}
          <div className={`flex items-center justify-between mb-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CrescentStarIcon size={13} color={isDarkMode ? '#fbbf24' : 'var(--p-hex)'} />
              <span className={`text-[10px] font-bold uppercase tracking-widest ${
                isDarkMode ? 'text-amber-400' : 'text-[var(--p-hex)]'
              }`}>{t('verse.verseOfDay')}</span>
            </div>
            <div className={`flex items-center gap-0.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button onClick={() => navigateVerse(-1)}
                className={`p-1 rounded-full transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-black/5'}`}>
                {isRTL ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => navigateVerse(1)}
                className={`p-1 rounded-full transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-black/5'}`}>
                {isRTL ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
              <button
                data-testid="verse-like-btn"
                onClick={toggleLike}
                disabled={likeLoading}
                className={`p-1 rounded-full transition-colors ${
                  isLiked ? 'text-rose-500' : isDarkMode ? 'text-gray-400 hover:text-rose-400 hover:bg-white/10' : 'text-gray-400 hover:text-rose-400 hover:bg-black/5'
                }`}
              >
                <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-rose-500' : ''}`} />
              </button>
            </div>
          </div>

          {/* Arabic — Naskh, compact (clamped to keep the card short) */}
          <p
            className={`text-center font-bold mb-1 line-clamp-2 ${isDarkMode ? 'text-amber-50' : 'text-[var(--p-hex)]'}`}
            style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '1rem', lineHeight: '1.65' }}
          >
            {currentVerse.arabic}
          </p>

          {/* Translation — follows selected language, clamped to 2 lines */}
          {language !== 'ar' && (
            <p className={`text-center text-xs font-medium leading-snug mb-1 line-clamp-2 ${
              isDarkMode ? 'text-gray-100' : 'text-gray-700'
            }`}>
              {translation}
            </p>
          )}

          {/* Reference */}
          <p className={`text-center text-[10px] font-semibold tracking-wide ${
            isDarkMode ? 'text-amber-400/75' : 'text-[var(--p-hex)]/60'
          }`}>
            ﴾ {currentVerse.reference} ﴿
          </p>
        </div>
      </div>
    );
  }

  // ── Full mode ────────────────────────────────────────────────────────────
  return (
    <div
      data-testid="quran-verse-card"
      className={`relative rounded-3xl overflow-hidden ${
        isDarkMode
          ? `bg-gradient-to-br from-[var(--card-dark-from)] to-[var(--card-dark-to)] border border-white/10 verse-glow`
          : `bg-gradient-to-br from-[var(--bg-light-from)] to-[var(--bg-light-via)] border border-[var(--p-hex)]/10 verse-glow-light`
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <VerseAnimatedBg isDarkMode={isDarkMode} />
      <div className="relative z-10 p-5">
        <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <CrescentStarIcon size={16} color={isDarkMode ? '#fbbf24' : 'var(--p-hex)'} />
            <span className={`text-xs font-semibold uppercase tracking-widest ${isDarkMode ? 'text-amber-400' : 'text-[var(--p-hex)]'}`}>
              {t('verse.verseOfDay')}
            </span>
          </div>
          <button
            data-testid="verse-like-btn"
            onClick={toggleLike}
            disabled={likeLoading}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              isLiked ? 'bg-rose-500 text-white' : isDarkMode ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-white/80 text-gray-600 hover:bg-white shadow-sm'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-white' : ''}`} />
            <span>{isLiked ? t('verse.saved') : t('verse.save')}</span>
          </button>
        </div>

        {/* Arabic — Naskh */}
        <p
          className={`text-center font-bold mb-4 ${isDarkMode ? 'text-amber-50' : 'text-[var(--p-hex)]'}`}
          style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '1.55rem', lineHeight: '2.2' }}
        >
          {currentVerse.arabic}
        </p>

        {/* Translation */}
        {language !== 'ar' && (
          <p className={`text-center text-base font-medium leading-relaxed mb-5 ${isDarkMode ? 'text-gray-100' : 'text-gray-700'}`}>
            {translation}
          </p>
        )}

        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button onClick={() => navigateVerse(-1)}
            className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-white/60'}`}>
            {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          <span className={`text-xs font-semibold tracking-wide ${isDarkMode ? 'text-amber-400/75' : 'text-[var(--p-hex)]/60'}`}>
            ﴾ {currentVerse.reference} ﴿
          </span>
          <button onClick={() => navigateVerse(1)}
            className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-white/60'}`}>
            {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuranVerseCard;
